import { Box, Container } from '@mui/material';
import { useIntl } from 'react-intl';
import OneTrustCookieSettings from '../../utils/OneTrustCookieSettings/OneTrustCookieSettings';
import FeedbackButton from '../feedback/FeedbackButton';
import BaseLink from '../link/Link';

function Link({
  to,
  children,
  external,
  action,
}: {
  to?: string;
  external?: boolean;
  children: string;
  action: string;
}) {
  return (
    <BaseLink
      action={action}
      external={external}
      to={to || ''}
      sx={{
        fontSize: '0.875rem',
        fontWeight: 500,
        textTransform: 'uppercase',
        flex: '0 0 auto',
        cursor: 'pointer',
        color: 'white.100',
        '&:hover': {
          color: 'orange.500',
        },
      }}
    >
      {children}
    </BaseLink>
  );
}

export default function Footer() {
  const { formatMessage } = useIntl();

  return (
    <Box
      component="footer"
      sx={(theme) => ({
        backgroundColor: theme.palette.blue[600],
        color: (theme) => theme.palette.white[100],
      })}
    >
      <Container>
        <Box py={5} m="0 auto" maxWidth={600}>
          <Box py={2} color="white" fontSize="2rem" textAlign="center">
            {formatMessage({ id: 'nav.feedback' })}
          </Box>
          <FeedbackButton />
        </Box>
        <Box
          px={5}
          py={2}
          maxWidth={1200}
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
        >
          <Box
            component="nav"
            aria-label="footer"
            flex="2 1"
            display="flex"
            justifyContent="flex-start"
            flexWrap="wrap"
            gap="16px"
          >
            <Link action="home" to="/">
              {formatMessage({ id: 'nav.home' })}
            </Link>
            <Link action="income-qualified" to="/income-qualified">
              {formatMessage({ id: 'nav.incomeQualified' })}
            </Link>
            <Link action="ev-owners" to="/ev-owners">
              {formatMessage({ id: 'nav.existingEvOwners' })}
            </Link>
            <Link action="rates" to="/rates">
              {formatMessage({ id: 'nav.rates' })}
            </Link>
            <Link action="vehicles" to="/vehicles">
              {formatMessage({ id: 'nav.vehicles' })}
            </Link>
            <Link action="compare" to="/compare">
              {formatMessage({ id: 'nav.compare' })}
            </Link>
            <Link action="incentives" to="/incentives">
              {formatMessage({ id: 'nav.incentives' })}
            </Link>
            <Link action="charging-stations" to="/charging-stations">
              {formatMessage({ id: 'nav.stations' })}
            </Link>
            <Link action="faq" to="/faq">
              {formatMessage({ id: 'nav.faq' })}
            </Link>
            <Link action="charging-faq" to="/charging-faq">
              {formatMessage({ id: 'nav.chargingFaq' })}
            </Link>
            <Link action="search" to="/search">
              {formatMessage({ id: 'nav.search' })}
            </Link>
            <Link
              external
              action="pge-company-info"
              to="https://www.pge.com/en_US/about-pge/company-information/privacy-policy/privacy.page"
            >
              {formatMessage({ id: 'nav.privacy' })}
            </Link>
            <OneTrustCookieSettings />
          </Box>
        </Box>
        <Box
          px={5}
          py={2}
          maxWidth={1200}
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
        >
          <Box flex="1 1" textAlign={{ xs: 'left', md: 'right' }} mt={{ xs: 2, md: 0 }}>
            <div>
              {formatMessage({ id: 'nav.copyRight' })}
              <br />
              {formatMessage({ id: 'nav.poweredBy' })}
            </div>
            <BaseLink
              external={true}
              to="https://www.zappyride.com/legal-notices"
              action="zr-legal-notices"
              sx={{
                fontSize: '0.875rem',
                flex: '0 0 auto',
                cursor: 'pointer',
                color: 'white.100',
                '&:hover': {
                  color: 'orange.500',
                },
              }}
            >
              {formatMessage({ id: 'nav.legalNotices' })}
            </BaseLink>
          </Box>
        </Box>
      </Container>
      <Box
        sx={(theme) => ({
          py: 5,
          textAlign: 'center',
          color: theme.palette.grey[300],
          backgroundColor: theme.palette.blue[800],
        })}
      >
        {formatMessage({ id: 'nav.copyright' }, { year: new Date(Date.now()).getFullYear() })}
      </Box>
    </Box>
  );
}
